/** Inspector primitives — property controls and mobile overlay fallback for
 *  whatever is selected in the content beside it, rather than replacing
 *  that content.
 *
 *  Desktop panels use ShellInspector and the app shell portals.
 *  The former page-level desktop docking and resizing implementation is retired.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type ElementType,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Input } from './input';
import { Label } from './label';
import { SelectContent, SelectItem, SelectTrigger } from './select';
import { Textarea } from './textarea';
import { cn } from './utils';

export interface InspectorHost {
  /** Attach to the element whose width decides docked vs. overlaid — the
   *  flex row holding the content and the Inspector, not the Inspector
   *  itself. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** True once the host is wide enough to dock the Inspector beside its
   *  content without squeezing that content under `siblingMinWidth`. */
  docks: boolean;
}

/** `dockThreshold` is the host width, in px, at which the Inspector switches
 *  from overlay to docked. Pass the same number you use as `minWidth` plus
 *  `siblingMinWidth` plus a little room, or just measure what looks right. */
export function useInspectorHost(dockThreshold: number): InspectorHost {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { containerRef, docks: width >= dockThreshold };
}

/** Compatibility options for ShellInspector. The app shell owns desktop sizing. */
export interface InspectorProps {
  side?: 'left' | 'right';
  open: boolean;
  docks: boolean;
  minWidth?: number;
  maxWidth?: number;
  siblingMinWidth?: number;
  resizable?: boolean;
  onWidthChange?: (width: number) => void;
  className?: string;
  children: ReactNode | ((state: { docks: boolean }) => ReactNode);
}

/** Canvas overlay for mobile, or touch surfaces that opt out of desktop docking. */
export function MobileInspectorOverlay({ open, className, children, forceOverlay = false }: Pick<InspectorProps, 'open' | 'className' | 'children'> & { forceOverlay?: boolean }) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  if (!open || (isDesktop && !forceOverlay)) return null;
  return <aside className={cn('absolute inset-0 z-10 flex flex-col overflow-hidden', className)}>
    {typeof children === 'function' ? children({ docks: false }) : children}
  </aside>;
}

/** Shell-owned left host; page editors continue to use ShellInspector. */
export function ShellLeftInspector({
  side = 'left',
  open,
  docks,
  minWidth = 400,
  maxWidth: panelMaxWidth,
  siblingMinWidth = 360,
  resizable = true,
  onWidthChange,
  className,
  children,
}: InspectorProps) {
  // The element's style is written directly while the pointer moves (120Hz,
  // no React state); the result commits to state on release. Every open
  // starts at minWidth; a drag lasts only while the Inspector stays open.
  const [width, setWidth] = useState(minWidth);
  // A ref, so a host passing a fresh arrow each render does not re-run the
  // open effect and snap a resized pane back to minWidth.
  const onWidthChangeRef = useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;
  useEffect(() => {
    if (!open) return;
    setWidth(minWidth);
    onWidthChangeRef.current?.(minWidth);
  }, [open, minWidth]);

  const paneRef = useRef<HTMLElement | null>(null);
  // Double-click on the handle returns the pane to its default width, the
  // way a split-view divider does. Width is state-driven, so setting it is
  // enough; the render writes the style back over any drag's direct write.
  const onResizeReset = useCallback(() => {
    setWidth(minWidth);
    onWidthChangeRef.current?.(minWidth);
  }, [minWidth]);
  const onResizeStart = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const pane = paneRef.current;
    const host = pane?.parentElement;
    if (!pane || !host) return;
    const startX = e.clientX;
    const startWidth = pane.getBoundingClientRect().width;
    // The sibling keeps at least siblingMinWidth, so the Inspector cannot
    // swallow it; on a host too narrow for both minimums the sibling wins.
    const maxWidth = Math.min(panelMaxWidth ?? Infinity, host.getBoundingClientRect().width - siblingMinWidth);
    let nextWidth = startWidth;
    const onMove = (ev: PointerEvent) => {
      nextWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + (side === 'left' ? 1 : -1) * (ev.clientX - startX)));
      pane.style.width = `${nextWidth}px`;
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setWidth(nextWidth);
      onWidthChangeRef.current?.(nextWidth);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    e.preventDefault();
  }, [minWidth, siblingMinWidth, side, panelMaxWidth]);

  if (!open) return null;

  return (
    <aside
      ref={paneRef}
      className={cn(
        'flex flex-col',
        docks
          ? cn('relative h-full shrink-0 overflow-visible border-border', side === 'left' ? 'border-r' : 'border-l')
          : 'absolute inset-0 z-10 overflow-hidden',
        className,
      )}
      style={{
        width: docks ? width : undefined,
        // The live ceiling for the host shrinking with the Inspector open:
        // the browser re-applies it on every layout pass, so the sibling
        // always keeps siblingMinWidth without extra plumbing.
        maxWidth: docks ? panelMaxWidth ? `min(${panelMaxWidth}px, calc(100% - ${siblingMinWidth}px))` : `calc(100% - ${siblingMinWidth}px)` : undefined,
      }}
    >
      {docks && resizable && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          title="Drag to resize. Double-click to reset."
          onPointerDown={onResizeStart}
          onDoubleClick={onResizeReset}
          className={cn('absolute inset-y-0 z-20 w-1.5 cursor-col-resize desktop-touch:touch-none desktop-touch:after:absolute desktop-touch:after:inset-y-0 desktop-touch:after:-left-[9px] desktop-touch:after:-right-[9px] desktop-touch:after:content-[\'\'] hover:bg-brand/40 active:bg-brand/60', side === 'left' ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2')}
        />
      )}
      {typeof children === 'function' ? children({ docks }) : children}
    </aside>
  );
}

export interface BottomInspectorProps {
  children: ReactNode;
  className?: string;
  /** A shell portal can own height, persistence and resizing for this surface. */
  sizing?: 'self' | 'host';
  /** Opens at this height and follows content-driven changes until resized. */
  initialHeight?: number;
  minHeight?: number;
  maxHeight?: number;
  /** Minimum drawing/content height kept above the bottom inspector. */
  siblingMinHeight?: number;
  labelledBy?: string;
}

function bottomInspectorBounds(host: HTMLElement | null, minHeight: number, maxHeight: number, siblingMinHeight: number) {
  const hostHeight = host?.getBoundingClientRect().height ?? 0;
  // An unmeasured host (SSR/tests) must not collapse the pane before layout.
  if (!host || hostHeight <= 0) return { min: minHeight, max: maxHeight };
  const rowGap = Number.parseFloat(getComputedStyle(host).rowGap) || 0;
  const available = Math.max(0, hostHeight - siblingMinHeight - rowGap);
  const max = Math.min(maxHeight, available);
  return { min: Math.min(minHeight, max), max };
}

function clampBottomInspectorHeight(value: number, bounds: { min: number; max: number }) {
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

/** The same Inspector surface, docked below its host's content rather than
 * beside it. Height is written directly during a drag, then committed once
 * on release so the content above does not re-render on every pointer move. */
export function BottomInspector({
  children,
  className,
  sizing = 'self',
  initialHeight = 320,
  minHeight = 180,
  maxHeight = 600,
  siblingMinHeight = 140,
  labelledBy,
}: BottomInspectorProps) {
  const [height, setHeight] = useState(initialHeight);
  const paneRef = useRef<HTMLElement | null>(null);
  const manuallyResizedRef = useRef(false);
  const preferredHeightRef = useRef(initialHeight);
  const [bounds, setBounds] = useState({ min: minHeight, max: maxHeight });

  useLayoutEffect(() => {
    if (sizing === 'host') return;
    const host = paneRef.current?.parentElement;
    if (!host) return;
    if (!manuallyResizedRef.current) preferredHeightRef.current = initialHeight;
    const sync = () => {
      const nextBounds = bottomInspectorBounds(host, minHeight, maxHeight, siblingMinHeight);
      setBounds(previous => previous.min === nextBounds.min && previous.max === nextBounds.max ? previous : nextBounds);
      setHeight(previous => {
        const next = clampBottomInspectorHeight(preferredHeightRef.current, nextBounds);
        return previous === next ? previous : next;
      });
    };
    sync();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(sync);
    observer.observe(host);
    return () => observer.disconnect();
  }, [initialHeight, minHeight, maxHeight, siblingMinHeight, sizing]);

  const onResizeReset = useCallback(() => {
    manuallyResizedRef.current = false;
    preferredHeightRef.current = initialHeight;
    setHeight(clampBottomInspectorHeight(initialHeight, bottomInspectorBounds(paneRef.current?.parentElement ?? null, minHeight, maxHeight, siblingMinHeight)));
  }, [initialHeight, minHeight, maxHeight, siblingMinHeight]);
  const onResizeStart = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const pane = paneRef.current;
    const host = pane?.parentElement;
    const handle = e.currentTarget;
    if (!pane || !host) return;
    const startY = e.clientY;
    const startHeight = pane.getBoundingClientRect().height;
    let nextHeight = startHeight;
    const onMove = (ev: PointerEvent) => {
      nextHeight = clampBottomInspectorHeight(startHeight + startY - ev.clientY, bottomInspectorBounds(host, minHeight, maxHeight, siblingMinHeight));
      pane.style.height = `${nextHeight}px`;
      handle.setAttribute('aria-valuenow', String(Math.round(nextHeight)));
      handle.setAttribute('aria-valuetext', `${Math.round(nextHeight)} pixels high`);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (nextHeight !== startHeight) {
        manuallyResizedRef.current = true;
        preferredHeightRef.current = nextHeight;
      }
      setHeight(nextHeight);
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    e.preventDefault();
  }, [maxHeight, minHeight, siblingMinHeight]);

  const onResizeKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    const currentBounds = bottomInspectorBounds(paneRef.current?.parentElement ?? null, minHeight, maxHeight, siblingMinHeight);
    let next: number;
    switch (e.key) {
      case 'ArrowUp': next = height + 16; break;
      case 'ArrowDown': next = height - 16; break;
      case 'Home': next = currentBounds.min; break;
      case 'End': next = currentBounds.max; break;
      default: return;
    }
    e.preventDefault();
    next = clampBottomInspectorHeight(next, currentBounds);
    manuallyResizedRef.current = true;
    preferredHeightRef.current = next;
    setHeight(next);
  }, [height, minHeight, maxHeight, siblingMinHeight]);

  return (
    <aside
      ref={paneRef}
      aria-labelledby={labelledBy}
      className={cn('relative z-30 flex min-h-0 shrink-0 flex-col overflow-hidden rounded-2xl bg-background text-foreground', className)}
      style={{ height: sizing === 'host' ? '100%' : height }}
    >
      {sizing === 'self' && <div
        role="separator"
        tabIndex={0}
        aria-orientation="horizontal"
        aria-label="Resize panel"
        aria-valuemin={Math.round(bounds.min)}
        aria-valuemax={Math.round(bounds.max)}
        aria-valuenow={Math.round(height)}
        aria-valuetext={`${Math.round(height)} pixels high`}
        title="Drag or use arrow keys to resize. Double-click to reset."
        onPointerDown={onResizeStart}
        onDoubleClick={onResizeReset}
        onKeyDown={onResizeKeyDown}
        className="group absolute inset-x-0 top-0 z-20 flex h-2 items-start justify-center cursor-row-resize desktop-touch:touch-none desktop-touch:after:absolute desktop-touch:after:inset-x-0 desktop-touch:after:-inset-y-2 desktop-touch:after:content-[''] focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand"
      >
        <span aria-hidden="true" className="mt-0.5 h-0.5 w-9 rounded-full bg-border group-hover:bg-brand/50 group-active:bg-brand/70" />
      </div>}
      {children}
    </aside>
  );
}

/**
 * Shared property-panel building blocks.
 *
 * The `property` variant follows compact desktop/browser property panes;
 * `sheet` remains the roomier touch/modal presentation. They measure
 * themselves against the panel, not the browser viewport, so a docked pane
 * cannot accidentally inherit a wide desktop form layout.
 */

export interface InspectorHeaderProps {
  /** Omit when peer workspace controls in `content` supply the heading. */
  title?: string;
  titleId?: string;
  subtitle?: string;
  leading?: ReactNode;
  /** Optional in-row content between the title and right-aligned actions. */
  content?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function InspectorHeader({
  title,
  titleId,
  subtitle,
  leading,
  content,
  actions,
  className,
}: InspectorHeaderProps) {
  return (
    <header className={cn('flex min-h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-3 py-2', className)}>
      {leading && <div className="shrink-0">{leading}</div>}
      {title && <div className={cn('min-w-0', content ? 'max-w-full shrink-0' : 'flex-1')}>
        <h2 id={titleId} className="truncate text-[length:var(--qs-capacity-copy-font,var(--text-menu))] font-semibold tracking-tight text-foreground" title={title}>{title}</h2>
        {subtitle && <p className="truncate text-xs text-muted-foreground" title={subtitle}>{subtitle}</p>}
      </div>}
      {content && <div className="min-w-0 flex-1">{content}</div>}
      {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
    </header>
  );
}

export interface InspectorFooterProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** Quiet operation state shown opposite the footer actions. */
  status?: ReactNode;
}

export function InspectorFooter({ children, status, className, ...props }: InspectorFooterProps) {
  return (
    <footer
      className={cn(
        'flex min-h-10 shrink-0 items-center gap-3 border-t border-border bg-background px-3 py-1.5',
        status ? 'justify-between' : 'justify-end',
        className,
      )}
      {...props}
    >
      {status && (
        <div className="min-w-0 text-xs text-muted-foreground" aria-live="polite" aria-atomic="true">
          {status}
        </div>
      )}
      <div className="flex shrink-0 items-center gap-2">
        {children}
      </div>
    </footer>
  );
}

export interface InspectorSectionProps {
  title: string;
  children: ReactNode;
  description?: string;
  icon?: ElementType;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  variant?: 'property' | 'sheet';
}

export function InspectorSection({
  title,
  children,
  description,
  icon: Icon,
  className,
  headerClassName,
  contentClassName,
  variant = 'property',
}: InspectorSectionProps) {
  return (
    <section className={cn(variant === 'property' ? 'border-b border-border last:border-b-0' : 'space-y-5', className)}>
      <div className={cn(
        'flex items-center gap-2',
        variant === 'property' ? 'min-h-10 border-b border-border/70 bg-muted/30 px-4 py-2' : 'px-1',
        headerClassName,
      )}>
        {Icon && <Icon className={cn('size-4 shrink-0', variant === 'property' ? 'text-muted-foreground' : 'text-brand')} aria-hidden="true" />}
        <div className="min-w-0">
          <h3 className={cn(
            'font-semibold',
            variant === 'property'
              ? 'text-xs text-foreground'
              : 'text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground/70',
          )}>{title}</h3>
          {description && <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{description}</p>}
        </div>
      </div>
      <div className={cn(
        variant === 'property' ? 'space-y-4 px-4 pb-5 pt-4' : 'space-y-5 border-t border-border/70 pt-5',
        contentClassName,
      )}>{children}</div>
    </section>
  );
}

export interface InspectorFieldProps {
  label: ReactNode;
  children: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  className?: string;
  labelClassName?: string;
  variant?: 'property' | 'sheet';
}

export function InspectorField({
  label,
  children,
  htmlFor,
  hint,
  className,
  labelClassName,
  variant = 'property',
}: InspectorFieldProps) {
  return (
    <div className={cn(variant === 'property' ? 'space-y-1.5' : 'space-y-2.5', className)}>
      <Label htmlFor={htmlFor} className={cn(
        'px-1 text-xs text-foreground/80',
        variant === 'property' ? 'font-semibold' : 'font-bold uppercase tracking-wider',
        labelClassName,
      )}>
        {label}
      </Label>
      {children}
      {hint && <p className="px-1 text-[11px] leading-4 text-muted-foreground">{hint}</p>}
    </div>
  );
}

export interface InspectorGridProps {
  children: ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
  variant?: 'property' | 'sheet';
}

export function InspectorGrid({ children, columns = 2, className, variant = 'property' }: InspectorGridProps) {
  return (
    <div data-slot="inspector-grid" data-columns={columns} className={cn(
      'grid',
      variant === 'property' ? 'gap-3' : 'gap-5',
      columns === 1
        ? 'grid-cols-1'
        : columns === 3
          ? variant === 'property' ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'
          : variant === 'property' ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2',
      className,
    )}>
      {children}
    </div>
  );
}

export interface InspectorRowProps {
  label: ReactNode;
  children?: ReactNode;
  hint?: ReactNode;
  className?: string;
  variant?: 'property' | 'sheet';
}

export function InspectorRow({
  label,
  children,
  hint,
  className,
  variant = 'property',
}: InspectorRowProps) {
  return (
    <div className={cn(
      'flex min-h-11 items-center justify-between gap-3 bg-background py-2.5',
      variant === 'property'
        ? 'border-b border-border/70 last:border-b-0'
        : 'rounded-xl border border-border/60 px-3',
      className,
    )}>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold leading-4 text-foreground/80">{label}</div>
        {hint && <div className="mt-0.5 text-[10px] leading-4 text-muted-foreground">{hint}</div>}
      </div>
      {children && <div className="shrink-0">{children}</div>}
    </div>
  );
}

export function InspectorControlFrame({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex h-8 items-center rounded-[var(--ui-control-radius)] border border-border bg-background px-2.5 py-1 text-xs shadow-none',
        'focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20',
        className,
      )}
      {...props}
    />
  );
}

export const InspectorInput = forwardRef<HTMLInputElement, ComponentProps<typeof Input>>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      className={cn(
        'h-8 rounded-[var(--ui-control-radius)] border border-border bg-background px-2.5 py-1 text-xs shadow-none',
        'transition-[border-color,box-shadow] focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:shadow-none',
        className,
      )}
      {...props}
    />
  ),
);
InspectorInput.displayName = 'InspectorInput';

/** Native-value picker with the shared Inspector control chrome. Keep a real
 *  select for short scope/view choices so mobile retains its platform picker. */
export const InspectorNativeSelect = forwardRef<HTMLSelectElement, ComponentProps<'select'>>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'h-8 rounded-[var(--ui-control-radius)] border border-border bg-background px-2.5 py-1 text-xs text-foreground shadow-none',
        'focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/20',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
InspectorNativeSelect.displayName = 'InspectorNativeSelect';

export const InspectorDateInput = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof InspectorInput>
>(({ type: _type, ...props }, ref) => (
  <InspectorInput
    ref={ref}
    type="date"
    {...props}
  />
));
InspectorDateInput.displayName = 'InspectorDateInput';

export function InspectorSelectTrigger({ className, size = 'sm', ...props }: ComponentProps<typeof SelectTrigger>) {
  return (
    <SelectTrigger
      size={size}
      className={cn(
        'h-8 rounded-[var(--ui-control-radius)] border-border bg-background px-2.5 py-1 text-xs shadow-none',
        'focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/20',
        className,
      )}
      {...props}
    />
  );
}

export function InspectorSelectContent({ className, ...props }: ComponentProps<typeof SelectContent>) {
  return <SelectContent className={cn('rounded-[var(--ui-menu-radius)] border-border bg-popover shadow-[var(--ui-menu-shadow)]', className)} {...props} />;
}

export function InspectorSelectItem({ className, ...props }: ComponentProps<typeof SelectItem>) {
  return <SelectItem className={cn('min-h-8 rounded-[var(--ui-menu-item-radius)] px-2 py-1.5 text-xs', className)} {...props} />;
}

export const InspectorTextarea = forwardRef<HTMLTextAreaElement, ComponentProps<typeof Textarea>>(
  ({ className, longDescription = false, ...props }, ref) => (
    <Textarea
      ref={ref}
      longDescription={longDescription}
      className={cn(
        longDescription ? 'min-h-24' : 'min-h-20',
        'rounded-[var(--ui-control-radius)] border-border bg-background px-2.5 py-2 text-xs leading-5 shadow-none',
        'focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/20',
        className,
      )}
      {...props}
    />
  ),
);
InspectorTextarea.displayName = 'InspectorTextarea';
